import React from "react";
import { Joyride, EVENTS, type EventHandler, type Step } from "react-joyride";
import { markDashboardWelcomeTourSeen } from "../../lib/dashboardWelcomeTourStorage";
import { DashboardTourTooltip } from "./DashboardTourTooltip";
import {
  DASHBOARD_WELCOME_TOUR_NARROW_QUERY,
  getDashboardWelcomeTourStepTemplates,
  getDashboardWelcomeTourTargetForStepIndex,
} from "./dashboardWelcomeTourSteps";

function subscribeDashboardTourNarrowViewport(onStoreChange: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }
  const mq = window.matchMedia(DASHBOARD_WELCOME_TOUR_NARROW_QUERY);
  mq.addEventListener("change", onStoreChange);
  return () => mq.removeEventListener("change", onStoreChange);
}

function getDashboardTourNarrowViewportSnapshot() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(DASHBOARD_WELCOME_TOUR_NARROW_QUERY).matches;
}

function scrollTourTargetIntoView(selector: string) {
  const el = document.querySelector(selector);
  if (!(el instanceof HTMLElement)) return;
  el.scrollIntoView({
    block: "center",
    inline: "nearest",
    behavior: "auto",
  });
}

/** Resolve `var(--x)` to pixels (works for `4rem`, `64px`, etc.). */
function readCssVarHeightPx(varName: string): number {
  if (typeof document === "undefined") return 0;
  const probe = document.createElement("div");
  probe.style.cssText = `position:absolute;left:0;top:0;visibility:hidden;height:var(${varName});width:0;margin:0;padding:0;border:0;pointer-events:none;`;
  document.body.appendChild(probe);
  const h = parseFloat(getComputedStyle(probe).height) || 0;
  document.body.removeChild(probe);
  return h;
}

const TAB_STRIP_MARGIN_PX = 20;

/** Escape Joyride/React passive effects before tab + layout (never use flushSync there). */
function yieldAfterReactCommit(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    }, 0);
  });
}

/**
 * First Y in the viewport where content is fully clear of the sticky Overview/Assistant row.
 * Uses the tab strip's live `getBoundingClientRect().bottom` so billing + sticky behavior match
 * what the user sees (header + tab height alone can under-count).
 */
function getNarrowTourClearTopBelowTabStripPx(): number {
  const tabStrip = document.querySelector("[data-dashboard-mobile-tabs]");
  if (tabStrip instanceof HTMLElement) {
    const r = tabStrip.getBoundingClientRect();
    if (r.height > 0 && r.width > 0) {
      return Math.ceil(r.bottom) + TAB_STRIP_MARGIN_PX;
    }
  }
  const headerPx = readCssVarHeightPx("--ds-app-header-height") || 64;
  const gapPx = readCssVarHeightPx("--ds-app-main-below-header-gap") || 0;
  return headerPx + gapPx + 52 + TAB_STRIP_MARGIN_PX;
}

async function afterLayoutScrollToSelector(selector: string) {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
  scrollTourTargetIntoView(selector);
}

export type DashboardWelcomeTourProps = {
  run: boolean;
  onClose: () => void;
  setMobileTab: React.Dispatch<
    React.SetStateAction<"overview" | "chat" | "preview">
  >;
};

export const DashboardWelcomeTour: React.FC<DashboardWelcomeTourProps> = ({
  run,
  onClose,
  setMobileTab,
}) => {
  const isNarrowViewport = React.useSyncExternalStore(
    subscribeDashboardTourNarrowViewport,
    getDashboardTourNarrowViewportSnapshot,
    () => false,
  );

  const stepsWithHooks = React.useMemo((): Step[] => {
    const templates = getDashboardWelcomeTourStepTemplates(isNarrowViewport);
    const siteUrlShiftTopPx = isNarrowViewport
      ? getNarrowTourClearTopBelowTabStripPx()
      : 0;

    return templates.map((template, index) => {
      const withSiteUrlShift: Partial<Step> =
        isNarrowViewport && index === 1 && template.floatingOptions
          ? {
              floatingOptions: {
                ...template.floatingOptions,
                shiftOptions: {
                  padding: {
                    top: siteUrlShiftTopPx,
                    bottom: 28,
                    left: 12,
                    right: 12,
                  },
                },
              },
              scrollOffset: siteUrlShiftTopPx,
            }
          : {};

      return {
        ...template,
        ...withSiteUrlShift,
        before: async () => {
          if (!isNarrowViewport) return;

          const tabForStep = index === 0 || index === 3 ? "chat" : "overview";
          setMobileTab(tabForStep);
          await yieldAfterReactCommit();
          const target = getDashboardWelcomeTourTargetForStepIndex(index, {
            narrow: true,
          });

          if (tabForStep === "chat") {
            if (target) await afterLayoutScrollToSelector(target);
            return;
          }

          if (index !== 1 && target) {
            await afterLayoutScrollToSelector(target);
          }
        },
      };
    });
  }, [isNarrowViewport, setMobileTab, run]);

  const handleEvent: EventHandler = (data) => {
    if (data.type === EVENTS.TOUR_START && isNarrowViewport) {
      queueMicrotask(() => {
        setMobileTab("chat");
      });
    }

    if (data.type === EVENTS.TOUR_END) {
      markDashboardWelcomeTourSeen();
      onClose();
    }
  };

  if (!run) {
    return null;
  }

  return (
    <Joyride
      run={run}
      steps={stepsWithHooks}
      continuous
      scrollToFirstStep={false}
      tooltipComponent={DashboardTourTooltip}
      onEvent={handleEvent}
      styles={{
        floater: {
          filter: "none",
          zIndex: 1100,
        },
        tooltip: {
          padding: 0,
          backgroundColor: "transparent",
          borderRadius: 0,
          boxShadow: "none",
          color: "inherit",
          width: "max-content",
          maxWidth: "min(420px, calc(100vw - 2rem))",
          minWidth: "min(100%, 288px)",
          boxSizing: "border-box",
        },
        tooltipContainer: {
          textAlign: "left",
          padding: 0,
        },
        tooltipContent: {
          padding: 0,
        },
        tooltipTitle: {
          margin: 0,
        },
        tooltipFooter: {
          margin: 0,
          padding: 0,
        },
        tooltipFooterSpacer: {
          flex: 1,
        },
        arrow: {
          color: "var(--color-card)",
        },
        overlay: {
          backgroundColor: "rgba(15, 23, 42, 0.4)",
        },
        spotlight: {},
      }}
      options={{
        zIndex: 1100,
        primaryColor: "var(--color-primary)",
        backgroundColor: "var(--color-card)",
        textColor: "var(--color-card-foreground)",
        arrowColor: "var(--color-card)",
        closeButtonAction: "skip",
        buttons: ["back", "close", "primary", "skip"],
        overlayColor: "rgba(15, 23, 42, 0.4)",
        spotlightRadius: 12,
        spotlightPadding: isNarrowViewport ? 10 : 8,
        showProgress: false,
      }}
      locale={{
        last: "Done",
        next: "Next",
        skip: "Skip tour",
        back: "Back",
      }}
    />
  );
};
